import { validateVideoRequest } from '../../../shared/video-config.js';
import { classifyGenerationFailure, copyGenerationErrorDetails, getGenerationErrorDetails, isSilentGenerationError } from './generation-error-details.js';

export const DEFAULT_VIDEO_PROJECT_TIMEOUT_MS = 20 * 60 * 1000;

/**
 * Track direct video projects that must be interrupted when the authenticated
 * account/session epoch changes. The invalidation promise resolves (rather than
 * rejects) so a switch that lands between project creation and caller wiring
 * cannot produce an unhandled rejection.
 */
export function createVideoSessionGuardRegistry() {
  const activeProjects = new Map();

  return {
    track(project) {
      if (!project?.id) {
        throw new Error('A started video project ID is required.');
      }

      let settleInvalidation;
      const invalidated = new Promise((resolve) => {
        settleInvalidation = resolve;
      });
      const guard = { project, settleInvalidation };
      activeProjects.set(project.id, guard);

      return {
        invalidated,
        release() {
          if (activeProjects.get(project.id) === guard) {
            activeProjects.delete(project.id);
          }
        }
      };
    },

    invalidate(message, code) {
      for (const [projectId, guard] of activeProjects) {
        const error = new Error(message || 'Sogni session changed during video generation.');
        error.code = code || 'SESSION_IDENTITY_CHANGED';
        error.projectId = projectId;
        error.unverified = true;
        activeProjects.delete(projectId);
        try {
          Promise.resolve(guard.project.cancel?.()).catch(() => {});
        } catch {
          // The local wait still settles even if cancellation cannot be issued.
        }
        guard.settleInvalidation(error);
      }
    },

    get size() {
      return activeProjects.size;
    }
  };
}

/** Strip wrapper-only lifecycle fields before calling the browser-native SDK. */
export function prepareVideoSdkProjectConfig(config, { appSource = 'sogni-vibe' } = {}) {
  const {
    numberOfMedia,
    waitForCompletion,
    timeout,
    autoResizeVideoAssets,
    onProgress,
    onJobCompleted,
    onJobFailed,
    billingMode,
    ...rest
  } = config;

  return {
    ...rest,
    type: 'video',
    numberOfMedia: numberOfMedia ?? 1,
    billingMode: billingMode || 'auto',
    appSource
  };
}

/** Wait for a started video project and request cancellation at the deadline. */
export async function waitForVideoProject(
  project,
  timeout = DEFAULT_VIDEO_PROJECT_TIMEOUT_MS,
  sessionInvalidated = null
) {
  if (!project?.waitForCompletion || !Number.isFinite(timeout) || timeout <= 0) {
    throw new Error('A started video project and positive timeout are required.');
  }

  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      try {
        Promise.resolve(project.cancel?.()).catch(() => {});
      } catch {
        // The timeout still rejects even if the SDK cannot issue cancellation.
      }
      const error = new Error('Video generation timed out. Cancellation was requested.');
      error.code = 'VIDEO_TIMEOUT';
      reject(error);
    }, timeout);
  });

  const waiters = [project.waitForCompletion(), timeoutPromise];
  if (sessionInvalidated && typeof sessionInvalidated.then === 'function') {
    waiters.push(Promise.resolve(sessionInvalidated).then((reason) => {
      if (reason instanceof Error) throw reason;
      const error = new Error(reason?.message || 'Sogni session changed during video generation.');
      error.code = reason?.code || 'SESSION_IDENTITY_CHANGED';
      throw error;
    }));
  }

  try {
    return await Promise.race(waiters);
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Reduce nested SDK errors to safe, useful messages for the Gallery banner. */
export function toFriendlyVideoGenerationError(error) {
  if (['VIDEO_TIMEOUT', 'VIDEO_RESULT_UNVERIFIED', 'VIDEO_STORAGE_UNAVAILABLE', 'CAPACITY_UNAVAILABLE'].includes(error?.code) || isSilentGenerationError(error)) return error;

  const nested = error?.originalError || error?.cause || error?.payload || error;
  const details = getGenerationErrorDetails(error);
  const code = details.code;
  const rawMessage = String(details.limitation || nested?.message || details.message || '').trim();
  const normalized = `${String(code || '')} ${rawMessage}`.toLowerCase();
  const kind = classifyGenerationFailure(error);
  let message = 'Video generation failed. Please try again.';

  if (code === 'REQUEST_CHANGED') {
    message = rawMessage || 'Your account or plan changed. Review the video again before generating.';
  } else if (kind === 'model-retired') {
    message = 'This video model has been retired. Choose a current video model.';
  } else if (kind === 'declined' || (details.originalCode === 'sensitiveContent' && kind !== 'filtered')) {
    // The platform's child-safety decline and a third-party model's own refusal
    // (which names the models to try) are final and shown as sent.
    message = rawMessage || message;
  } else if (['queue', 'billing-recovery', 'plan-limit', 'trial-limit', 'fair-use', 'entitlement', 'payment-verification', 'request-cost'].includes(kind)) {
    message = rawMessage || message;
  } else if (normalized.includes('balance') || normalized.includes('insufficient')) {
    message = rawMessage || 'Insufficient balance. Check your account credits.';
  } else if (normalized.includes('premium') || normalized.includes('4054')) {
    message = 'This encoder requires Premium Spark.';
  } else if (normalized.includes('free spark')) {
    message = 'This encoder is not available with Free Spark.';
  } else if (kind === 'filtered' || normalized.includes('nsfw') || normalized.includes('filter')) {
    // The socket's default withhold text names neither word; the classifier knows it.
    message = 'Content was filtered by the safety policy. Try adjusting the prompt.';
  } else if (
    normalized.includes('no workers') ||
    normalized.includes('model not found') ||
    normalized.includes('not available')
  ) {
    message = rawMessage || 'The selected video model is not available right now.';
  } else if (normalized.includes('timeout')) {
    message = rawMessage || 'Video generation timed out. Please try again.';
  } else if (
    normalized.includes('network') ||
    normalized.includes('socket') ||
    normalized.includes('connection')
  ) {
    message = 'Video generation lost its connection. Please try again.';
  } else if (
    normalized.includes('session_identity_changed') ||
    normalized.includes('session_ended') ||
    normalized.includes('account changed') ||
    normalized.includes('signed out')
  ) {
    message = rawMessage || 'Your Sogni session changed. Start the video again.';
  } else if (normalized.includes('validation') || normalized.includes('invalid')) {
    message = rawMessage || message;
  } else if (
    normalized.includes('prepare the selected image') ||
    normalized.includes('image could not be decoded') ||
    normalized.includes('gallery image')
  ) {
    message = rawMessage || message;
  }

  const friendly = copyGenerationErrorDetails(new Error(message), error);
  friendly.code = code || 'VIDEO_GENERATION_FAILED';
  return friendly;
}

/**
 * Build the browser SDK payload for a validated image-to-video render.
 * Kept pure so the exact billing/model/timing contract can be unit-tested
 * without connecting to Sogni or starting the app.
 */
export function buildVideoProjectConfig({
  referenceImage,
  prompt,
  modelId,
  width,
  height,
  fps,
  durationSeconds,
  modelParameters,
  generateAudio,
  network = 'fast',
  ratio,
  disableNSFWFilter = true,
  seed,
  timeout = DEFAULT_VIDEO_PROJECT_TIMEOUT_MS
} = {}) {
  if (!referenceImage) {
    throw new Error('A reference image is required.');
  }
  if (typeof prompt !== 'string' || !prompt.trim()) {
    throw new Error('Describe how the image should move.');
  }
  if (!Number.isFinite(timeout) || timeout <= 0) {
    throw new Error('Video project timeout must be a positive number.');
  }

  const settings = validateVideoRequest({
    modelId,
    width,
    height,
    fps,
    durationSeconds,
    ...(modelParameters ? { catalogModel: { parameters: modelParameters } } : {})
  });

  return {
    settings,
    projectConfig: {
      type: 'video',
      modelId: settings.modelId,
      ...(settings.model.steps !== undefined ? { steps: settings.model.steps } : {}),
      ...(settings.model.guidance !== undefined ? { guidance: settings.model.guidance } : {}),
      ...(settings.model.sampler !== undefined ? { sampler: settings.model.sampler } : {}),
      ...(settings.model.scheduler !== undefined ? { scheduler: settings.model.scheduler } : {}),
      ...(settings.model.shift !== undefined ? { shift: settings.model.shift } : {}),
      ...(settings.model.strength !== undefined ? { strength: settings.model.strength } : {}),
      positivePrompt: prompt.trim(),
      negativePrompt: '',
      stylePrompt: '',
      numberOfMedia: 1,
      ...(seed !== undefined ? { seed } : {}),
      referenceImage,
      // H3's shortest clip is 124/24 seconds. The exact frame grid is also
      // what the estimator receives; passing a whole 5s duration would fail SDK validation.
      ...(settings.family === 'minimax' ? { frames: settings.frames } : { duration: settings.durationSeconds }),
      fps: settings.fps,
      width: settings.width,
      height: settings.height,
      generateAudio: settings.model.supportsNativeAudio && (settings.model.nativeAudioAlwaysOn || generateAudio !== false),
      network,
      disableNSFWFilter,
      ...(ratio && settings.family === 'wan3' ? { ratio } : {}),
      ...(settings.family === 'wan3' ? { promptExtend: false } : {}),
      tokenType: 'spark'
    }
  };
}
